"use client"

import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { cn } from "@/lib/utils"
import { SearchIcon } from "@/components/ui/icons"
import { useSearchModal } from "@/components/search/search-provider"
import { useCountry } from "@/components/layout/country-provider"
import { track } from "@/lib/analytics"
import { ClientOnly } from "@/lib/animation/client-only"

interface SearchTriggerProps {
  /**
   * Where the trigger lives — used by analytics. Currently only the
   * desktop navbar surface is rendered as a `<SearchTrigger>`; the mobile
   * menu owns its own search button (closes the menu + opens the modal
   * instantly) and tags itself with `surface: "navbar-mobile"` directly.
   */
  surface: "navbar-desktop"
  /**
   * Optional override — desktop variant defaults to dark icon, but on the
   * homepage hero (transparent navbar) the resting icon should be white.
   * The navbar computes its own `isTransparent` flag and forwards it here.
   */
  transparent?: boolean
  className?: string
}

/**
 * Desktop navbar search. A pinned magnifier icon that opens the full search
 * modal on click (the same modal Cmd-K opens from anywhere).
 *
 * It used to bloom into an inline pill on hover, but that pill grew LEFT over
 * its neighbour and covered the locale picker you were reaching for — a
 * hover-to-open control expanding over an adjacent target. Now it's a plain
 * click target: no hover expansion, nothing overlaps the locale picker, and
 * the modal (more room, ranked results, keyboard nav) is the one search
 * surface. `country`/`locale` are read only for the `search_opened`
 * analytics tags.
 */
export function SearchTrigger({
  surface,
  transparent = false,
  className,
}: SearchTriggerProps) {
  const t = useTranslations("search")
  const { open: openModal } = useSearchModal()
  const locale = useLocale()
  const { code: country } = useCountry()

  const handleOpenModal = () => {
    track.searchOpened({ source: surface, country, locale })
    openModal({ source: surface })
  }

  return (
    <span
      className={cn(
        "relative hidden h-10 w-10 flex-shrink-0 md:inline-flex",
        className
      )}
    >
      {/*
        Mount the interactive button client-side only. The enclosing span
        reserves the 40×40 slot at SSR (no CLS), but the button — and its
        onClick — only paint after hydration commits, so the visible trigger
        is never a dead click target during the SSR-paint-before-JS window.
        On the homepage two Navbars render simultaneously (mobile + desktop,
        CSS-toggled); each hydrates independently, widening that race. See
        AHD-6514.
      */}
      <ClientOnly>
        <button
          type="button"
          onClick={handleOpenModal}
          aria-label={t("openLabel")}
          className={cn(
            "flex h-10 w-10 flex-shrink-0 cursor-pointer items-center justify-center rounded-full transition-colors duration-200",
            transparent
              ? "text-white/90 hover:bg-white/10 hover:text-white"
              : "text-neutral-900 hover:bg-white"
          )}
        >
          <SearchIcon width={20} height={20} />
        </button>
      </ClientOnly>
    </span>
  )
}
